<template>
  <el-card shadow="hover" class="box-card">
    <template #header>
      <div class="el-row el-align-middle el-justify-space-between clearfix">
        <span class="card-head">Voice Insights</span>
        <el-button
          size="small"
          type="text"
          class="pull-right"
          icon="el-icon-refresh"
          :disabled="loadingTranscripts"
          :loading="loadingTranscripts"
          @click="onRefreshTranscripts"
        >
          Refresh
        </el-button>
      </div>
    </template>

    <el-table
      ref="voiceTable"
      :data="paginatedTranscripts"
      v-loading="loadingTranscripts"
      element-loading-text="Loading..."
      row-key="id"
      @selection-change="handleSelectionChange"
      style="width: 100%"
    >
      <el-table-column type="selection" width="50" reserve-selection />

      <el-table-column prop="score" label="Score" width="150">
        <template v-slot="{ row }">
          <el-rate
            v-model="row.score"
            disabled
            :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
            text-color="#ff9900"
            :size="12"
          />
        </template>
      </el-table-column>

      <el-table-column prop="sender" label="Sender" width="180">
        <template v-slot="{ row }">
          <span class="sender-name"> <i class="el-icon-user"></i> {{ row.sender }} </span>
          <span v-if="row.location" class="sender-info">
            <i class="el-icon-location"></i> {{ row.location }}
          </span>
          <span class="sender-info">
            <i class="el-icon-time"></i> {{ formatDate(row.timestamp) }}
          </span>
        </template>
      </el-table-column>

      <el-table-column prop="summary" label="Summary" min-width="100">
        <template v-slot="{ row }">
          <div class="summary">"{{ previewSummary(row.transcript) }}"</div>
        </template>
      </el-table-column>

      <el-table-column prop="tags" label="Tags" width="140">
        <template v-slot="{ row }">
          <div class="tags-cell">
            <el-tag v-for="tag in row.tags" :key="tag" size="mini">
              {{ tag }}
            </el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="tone" label="Tone" width="80">
        <template v-slot="{ row }">
          <el-tag v-if="row.tone" size="mini">
            {{ capitalize(row.tone) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Actions" width="180" align="center">
        <template v-slot="{ row }">
          <el-button-group class="action-group">
            <el-tooltip content="Preview Audio" placement="top">
              <el-button size="mini" icon="fa fa-eye" @click="onPreviewAudio(row)" />
            </el-tooltip>

            <el-tooltip
              :content="row.approved ? 'Approved for On-Air' : 'Approve for On-Air'"
              placement="top"
            >
              <el-button
                size="mini"
                :icon="row.approved ? 'fa fa-podcast' : 'el-icon-success'"
                @click="onApprove(row)"
              />
            </el-tooltip>

            <el-tooltip :content="row.flagged ? 'Unflag' : 'Flag for Review'" placement="top">
              <el-button
                size="mini"
                :icon="row.flagged ? 'fa fa-flag' : 'fa fa-flag-o'"
                @click="onToggleFlag(row)"
              />
            </el-tooltip>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <span class="text-muted">
        Showing page {{ this.currentPage }} to {{ this.totalViews }} of {{ totalTranscripts }} voice
        notes
      </span>

      <el-pagination
        background
        layout="prev, pager, next"
        :pager-count="pageCount"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="totalTranscripts"
        @current-change="onPageChange"
      />
    </div>
  </el-card>
</template>

<script>
import moment from 'moment';
import { capitalize } from 'lodash';

export default {
  name: 'VoiceNotesTable',
  props: {
    transcripts: {
      type: Array,
      required: true,
    },
    loadingTranscripts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageSize: 3,
      pageCount: 5,
      currentPage: 1,
      selectedRows: [],
    };
  },
  computed: {
    totalTranscripts() {
      return this.transcripts.length;
    },
    totalViews() {
      return Math.ceil(this.totalTranscripts / this.pageSize);
    },
    paginatedTranscripts() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = Math.min(startIndex + this.pageSize, this.totalTranscripts);
      return this.transcripts.slice(startIndex, endIndex);
    },
  },
  methods: {
    capitalize,
    formatDate(timestamp) {
      return moment(timestamp).format('DD MMM YYYY, HH:mm');
    },
    previewSummary(text) {
      if (!text) return '';
      return text.length > 70 ? `${text.slice(0, 70)}...` : text;
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
      this.$emit('selection-change', selection);
    },
    onToggleFlag(row) {
      this.$emit('toggle-flag', row);
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    onPreviewAudio(row) {
      this.$emit('preview-audio', row);
    },
    onApprove(row) {
      this.$emit('approve-on-air', row);
    },
    onClearSelection() {
      if (this.$refs.voiceTable) {
        this.$refs.voiceTable.clearSelection();
        this.selectedRows = [];
      }
    },
    onRefreshTranscripts() {
      this.$emit('refresh-transcripts');
    },
  },
};
</script>

<style scoped>
.sender-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.sender-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.summary {
  font-size: 11px;
  color: #909399;
  font-style: italic;
  line-height: 1.4;
}

.tags-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.action-group {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding: 0 8px;
}

:deep(.el-table__row td) {
  vertical-align: middle;
}

:deep(.el-table .cell) {
  word-break: normal;
  line-height: 1.4;
  white-space: normal;
}

:deep(.el-table td) {
  height: auto !important;
}

:deep(.el-rate) {
  margin-bottom: 0;
}
</style>


// WEBPACK FOOTER //
// VoiceNotesTable.vue?50c62f99